/**
 * One batch request per page of history, cached until five minutes before the
 * URL expires. The cache matters: every URL the API issues writes an audit
 * row, so refetching on every scroll would inflate the audit table.
 *
 * Module state, not a ref: a message that unmounts on scroll must not re-ask.
 * Every mounted hook subscribes, so a request that finishes after its channel
 * unmounted and reopened still re-renders the view that shows its tiles.
 */

import { useCallback, useEffect, useState } from 'react';

import {
  getChatAttachmentDownload,
  getChatAttachmentUrls,
} from '../../services/api';
import { useStore } from '../../store';
import { getBusinessErrorMessage } from '../../utils/apiErrorDetail';

export interface AttachmentUrl {
  url: string;
  previewUrl?: string;
  expiresAtMs: number;
}

const REFRESH_MARGIN_MS = 5 * 60 * 1000;
// The batch route's own cap (AttachmentUrlsIn); a larger page is split.
export const URL_BATCH_MAX = 100;

const cache = new Map<string, AttachmentUrl>();
// Ids the server left out: outside the caller's channels. Not retried.
const absent = new Set<string>();
// Id -> the generation its request started at.
const inFlight = new Map<string, number>();
// Bumped by every invalidation, so a response that started earlier is dropped.
const generation = new Map<string, number>();
// Ready ids already refreshed once for a missing preview URL; stops a loop.
const refreshedForReady = new Set<string>();
const listeners = new Set<() => void>();

// A failed batch: when each id may be asked again, and how many failures in a
// row it has had. The wait doubles, so a broken URL route is polled a handful
// of times, never asked once per render.
const RETRY_BASE_MS = 2 * 1000;
const RETRY_MAX_MS = 60 * 1000;
const retryAt = new Map<string, number>();
const failures = new Map<string, number>();
// One timer for the soonest retry; the render it wakes asks again.
let wakeTimer: ReturnType<typeof setTimeout> | undefined;
let wakeAt = 0;

const notify = () => listeners.forEach(listener => listener());

const scheduleWake = (at: number): void => {
  if (wakeTimer !== undefined && wakeAt <= at) return;
  if (wakeTimer !== undefined) clearTimeout(wakeTimer);
  wakeAt = at;
  wakeTimer = setTimeout(
    () => {
      wakeTimer = undefined;
      notify();
    },
    Math.max(0, at - Date.now())
  );
};

const generationOf = (id: string): number => generation.get(id) ?? 0;

const fresh = (id: string): AttachmentUrl | undefined => {
  const entry = cache.get(id);
  return entry && entry.expiresAtMs - REFRESH_MARGIN_MS > Date.now()
    ? entry
    : undefined;
};

/** Forget one id so the next `urlsFor` asks again — used when a preview lands. */
export const invalidateAttachmentUrl = (id: string): void => {
  cache.delete(id);
  absent.delete(id);
  inFlight.delete(id);
  refreshedForReady.delete(id);
  retryAt.delete(id);
  failures.delete(id);
  generation.set(id, generationOf(id) + 1);
};

const fetchBatch = async (ids: string[]): Promise<void> => {
  const started = new Map(ids.map(id => [id, generationOf(id)]));
  ids.forEach(id => inFlight.set(id, started.get(id)!));
  const current = (id: string) => generationOf(id) === started.get(id);
  try {
    const { data } = await getChatAttachmentUrls({
      body: { attachment_ids: ids },
      throwOnError: true,
    });
    ids.filter(current).forEach(id => {
      retryAt.delete(id);
      failures.delete(id);
      const entry = data[id];
      if (!entry) {
        absent.add(id);
        return;
      }
      cache.set(id, {
        url: entry.url,
        previewUrl: entry.preview_url ?? undefined,
        expiresAtMs: new Date(entry.expires_at).getTime(),
      });
    });
  } catch (error) {
    // A network or server fault. Hold the ids back before any listener runs:
    // a consumer calls `urlsFor` in its render body, so an id left askable
    // would be requested again by the very render this failure causes, and
    // again by that one's failure — a storm bounded only by latency.
    ids.filter(current).forEach(id => {
      const count = (failures.get(id) ?? 0) + 1;
      failures.set(id, count);
      retryAt.set(
        id,
        Date.now() + Math.min(RETRY_BASE_MS * 2 ** (count - 1), RETRY_MAX_MS)
      );
    });
    console.error('Could not load attachment URLs', error);
  } finally {
    ids.forEach(id => {
      if (inFlight.get(id) === started.get(id)) inFlight.delete(id);
    });
    // Either way the request has ended: every consumer re-renders, so a tile
    // is not left waiting on it. An id invalidated meanwhile is asked again;
    // an id that just failed waits for its retry time.
    notify();
  }
};

export const useAttachmentUrls = () => {
  const [, forceRender] = useState(0);

  useEffect(() => {
    const listener = () => forceRender(tick => tick + 1);
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  }, []);

  /**
   * `readyIds`: attachments the history says are `ready`. A cached pair from
   * before the preview existed has no preview URL; whatever path delivered
   * the `ready` status (the frame, a reset, a visibility refetch, a reopen),
   * that pair is refreshed once.
   */
  const urlsFor = useCallback((ids: string[], readyIds: string[] = []) => {
    readyIds.forEach(id => {
      const entry = cache.get(id);
      if (entry && !entry.previewUrl && !refreshedForReady.has(id)) {
        invalidateAttachmentUrl(id);
        refreshedForReady.add(id);
      }
    });
    const now = Date.now();
    const missing = [
      ...new Set(
        ids.filter(
          id =>
            !fresh(id) &&
            !absent.has(id) &&
            !inFlight.has(id) &&
            (retryAt.get(id) ?? 0) <= now
        )
      ),
    ];
    for (let start = 0; start < missing.length; start += URL_BATCH_MAX) {
      void fetchBatch(missing.slice(start, start + URL_BATCH_MAX));
    }
    // Nothing else would ask again: wake the consumers when the soonest of
    // these ids is askable, so a failure recovers without the user acting.
    const cooling = ids.map(id => retryAt.get(id) ?? 0).filter(at => at > now);
    if (cooling.length > 0) scheduleWake(Math.min(...cooling));
    return Object.fromEntries(
      ids.map(id => [id, fresh(id) ?? (absent.has(id) ? null : undefined)])
    ) as Record<string, AttachmentUrl | null | undefined>;
  }, []);

  const download = useCallback(async (id: string) => {
    // Open the tab before the await: mobile Safari and Chrome block a popup
    // opened from an async continuation, outside the click's user gesture.
    // `noopener` would make `open` return null, so the opener is cut by hand.
    const tab = window.open('', '_blank');
    if (tab) tab.opener = null;
    try {
      const { data } = await getChatAttachmentDownload({
        path: { attachment_id: id },
        throwOnError: true,
      });
      if (tab) {
        tab.location.href = data.url;
      } else {
        window.open(data.url, '_blank', 'noopener,noreferrer');
      }
    } catch (error) {
      tab?.close();
      console.error('Could not download attachment', error);
      useStore
        .getState()
        .showError(
          'Could not download the file',
          getBusinessErrorMessage(error) ?? undefined
        );
    }
  }, []);

  return { urlsFor, download };
};
